import { useState, useEffect, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { isIOS, openAssessmentPdfIOS } from "@/lib/pdf";

const IOS = isIOS();
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import ReferenceTable from "@/components/ReferenceTable";
import EvolutionChart from "@/components/EvolutionChart";
import {
  Search, User, Ruler, Dumbbell, Activity, HeartPulse, FileDown, Loader2, Save, X, Calculator, Trash2, ClipboardList,
} from "lucide-react";
import {
  ageFromBirth, calcImc, colorImc, colorFat, colorMuscle, colorVisceral, colorBodyAge, colorClasses, colorLabel,
} from "@/lib/avaliacaoRef";

const MEASURE_FIELDS = [
  ["neck", "Pescoço"], ["shoulder", "Ombro"], ["chest", "Peito"], ["belly", "Barriga"],
  ["arm_right", "Braço Direito"], ["arm_left", "Braço Esquerdo"],
  ["thigh_right", "Coxa Direita"], ["thigh_left", "Coxa Esquerda"],
  ["calf_right", "Panturrilha Direita"], ["calf_left", "Panturrilha Esquerda"],
];

const SectionCard = ({ icon: Icon, title, enabled, onToggle, testid, children }) => (
  <div className={`rounded-3xl border p-5 sm:p-6 transition-colors ${enabled ? "bg-[#181F2C] border-[#263147]" : "bg-[#141A24] border-[#1E2634]"}`}>
    <div className="flex items-center justify-between mb-1">
      <h2 className="text-base sm:text-lg font-semibold font-heading text-slate-100 flex items-center gap-2.5">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-slate-700/40 text-slate-500"}`}><Icon className="w-5 h-5" /></span>
        {title}
      </h2>
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-500">{enabled ? "Ativado" : "Desativado"}</span>
        <Switch data-testid={testid} checked={enabled} onCheckedChange={onToggle} className="data-[state=checked]:bg-emerald-500" />
      </div>
    </div>
    {enabled && <div className="mt-4">{children}</div>}
  </div>
);

const ColorBadge = ({ color }) => color ? (
  <span className={`text-[11px] px-2 py-0.5 rounded-full border ${colorClasses[color]}`}>{colorLabel[color]}</span>
) : null;

const emptyState = {
  blocks: { measurements: true, tests: true, bioimpedance: true, health: true },
  measurements: {}, tests: { choice: "abdominal" }, bio: {}, health: { pressure: "normal" },
};

export default function AvaliacaoFisica() {
  const [students, setStudents] = useState([]);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(null);
  const [history, setHistory] = useState([]);
  const [st, setSt] = useState(emptyState);
  const [saving, setSaving] = useState(false);
  const [pdfId, setPdfId] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);

  const loadStudents = useCallback(async () => {
    try {
      const { data } = await api.get("/students", { params: { status: "ativo", search: query } });
      setStudents(data);
    } catch (e) { console.error("Falha ao carregar alunos:", e); }
  }, [query]);

  useEffect(() => { const t = setTimeout(loadStudents, 250); return () => clearTimeout(t); }, [loadStudents]);

  const loadHistory = async (sid) => {
    try { const { data } = await api.get("/assessments", { params: { student_id: sid } }); setHistory(data); }
    catch (_) { setHistory([]); }
  };

  const selectStudent = (s) => {
    setSelected(s); setQuery(""); setStudents([]); setSt(emptyState); setPdfId(null);
    loadHistory(s.id);
  };

  const age = selected ? ageFromBirth(selected.birth_date) : null;
  const setBlock = (k, v) => setSt((p) => ({ ...p, blocks: { ...p.blocks, [k]: v } }));
  const setM = (k, v) => setSt((p) => ({ ...p, measurements: { ...p.measurements, [k]: v } }));
  const setT = (k, v) => setSt((p) => ({ ...p, tests: { ...p.tests, [k]: v } }));
  const setB = (k, v) => setSt((p) => ({ ...p, bio: { ...p.bio, [k]: v } }));
  const setH = (k, v) => setSt((p) => ({ ...p, health: { ...p.health, [k]: v } }));

  const imc = calcImc(st.bio.weight, st.bio.height);

  const buildPayload = () => ({
    student_id: selected.id,
    blocks_enabled: st.blocks,
    measurements: st.blocks.measurements ? st.measurements : {},
    tests: st.blocks.tests ? st.tests : {},
    bioimpedance: st.blocks.bioimpedance ? st.bio : {},
    health: st.blocks.health ? st.health : {},
  });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/assessments", buildPayload());
      toast.success("Avaliação registrada com sucesso!");
      setPdfId(data.id);
      await loadHistory(selected.id);
      return data.id;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao salvar");
      return null;
    } finally { setSaving(false); }
  };

  const downloadPdf = async (id) => {
    let targetId = id || pdfId;
    if (IOS) {
      try { await openAssessmentPdfIOS(targetId || save); } catch (_) { toast.error("Falha ao gerar PDF"); }
      return;
    }
    if (!targetId) { targetId = await save(); if (!targetId) return; }
    try {
      const res = await api.get(`/assessments/${targetId}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `avaliacao-${selected.nickname || selected.full_name}.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
      toast.success("PDF gerado!");
    } catch (_) { toast.error("Falha ao gerar PDF"); }
  };

  const confirmDelete = async () => {
    try { await api.delete(`/assessments/${pendingDelete.id}`); toast.success("Avaliação excluída"); setPendingDelete(null); loadHistory(selected.id); }
    catch (_) { toast.error("Falha ao excluir"); }
  };

  const bioInput = (key, label, unit, colorFn) => {
    const color = colorFn ? colorFn(st.bio[key]) : null;
    return (
      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-slate-300 text-sm">{label}</Label>
          <ColorBadge color={color} />
        </div>
        <div className="relative">
          <Input type="number" step="0.1" data-testid={`bio-${key}`} value={st.bio[key] ?? ""} onChange={(e) => setB(key, e.target.value)}
            className={`h-11 bg-[#0F1520] text-slate-100 rounded-xl pr-12 focus-visible:ring-emerald-500 ${color === "red" ? "border-red-500/60" : color === "green" ? "border-emerald-500/50" : "border-[#263147]"}`} />
          {unit && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-500">{unit}</span>}
        </div>
      </div>
    );
  };

  return (
    <div>
      <h1 className="text-2xl sm:text-3xl font-bold font-heading text-slate-100 mb-1">Avaliação Física</h1>
      <p className="text-sm text-slate-400 mb-6">Selecione o aluno, ative os blocos desejados e gere o relatório.</p>

      {/* Student search */}
      {!selected ? (
        <div className="max-w-lg">
          <div className="relative">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <Input data-testid="assessment-student-search" value={query} onChange={(e) => setQuery(e.target.value)} autoFocus
              placeholder="Digite o nome do aluno..." className="h-12 pl-10 bg-[#181F2C] border-[#263147] text-slate-100 rounded-xl focus-visible:ring-emerald-500" />
          </div>
          <div className="mt-2 space-y-1" data-testid="assessment-student-results">
            {students.map((s) => (
              <button key={s.id} data-testid={`assessment-pick-${s.id}`} onClick={() => selectStudent(s)}
                className="w-full text-left px-4 py-3 rounded-xl bg-[#181F2C] border border-[#263147] hover:border-emerald-500/50 transition-colors flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#0F1520] flex items-center justify-center"><User className="w-4 h-4 text-slate-500" /></div>
                <div><p className="text-sm font-medium text-slate-100">{s.full_name}</p><p className="text-xs text-slate-500">"{s.nickname}"</p></div>
              </button>
            ))}
            {query && students.length === 0 && <p className="text-sm text-slate-500 px-2 py-3">Nenhum aluno encontrado.</p>}
          </div>
        </div>
      ) : (
        <div className="space-y-6 pb-28">
          {/* Basic data */}
          <div className="bg-[#181F2C] border border-[#263147] rounded-3xl p-5 flex flex-col sm:flex-row sm:items-center gap-4" data-testid="assessment-basic-data">
            <div className="w-14 h-14 rounded-2xl bg-[#0F1520] border border-[#263147] flex items-center justify-center shrink-0"><User className="w-6 h-6 text-slate-500" /></div>
            <div className="flex-1 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div><p className="text-xs text-slate-500">Nome</p><p className="text-sm font-medium text-slate-100">{selected.full_name}</p></div>
              <div><p className="text-xs text-slate-500">Sexo</p><p className="text-sm font-medium text-slate-100">{selected.gender || "—"}</p></div>
              <div><p className="text-xs text-slate-500">Idade</p><p className="text-sm font-medium text-slate-100">{age !== null ? `${age} anos` : "—"}</p></div>
              <div><p className="text-xs text-slate-500">Frequência</p><p className="text-sm font-medium text-slate-100">{selected.weekly_frequency || "—"}</p></div>
            </div>
            <Button variant="outline" data-testid="assessment-change-student" onClick={() => { setSelected(null); setHistory([]); }} className="border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Trocar aluno</Button>
          </div>

          {age === null && (
            <div data-testid="birthdate-hint" className="flex items-center gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl px-4 py-2.5">
              <HeartPulse className="w-4 h-4 shrink-0" /> Cadastre a data de nascimento do aluno para habilitar as cores de referência (gordura, massa muscular e idade corporal).
            </div>
          )}

          {/* Block 1 - Measurements */}
          <SectionCard icon={Ruler} title="Medidas Corporais (cm)" enabled={st.blocks.measurements} onToggle={(v) => setBlock("measurements", v)} testid="toggle-measurements">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {MEASURE_FIELDS.map(([k, lb]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">{lb}</Label>
                  <Input type="number" step="0.1" data-testid={`measure-${k}`} value={st.measurements[k] ?? ""} onChange={(e) => setM(k, e.target.value)}
                    className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl focus-visible:ring-emerald-500" placeholder="cm" />
                </div>
              ))}
            </div>
          </SectionCard>

          {/* Block 2 - Tests */}
          <SectionCard icon={Dumbbell} title="Testes Físicos" enabled={st.blocks.tests} onToggle={(v) => setBlock("tests", v)} testid="toggle-tests">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Flexão de braço (qtd em 1 min)</Label>
                <Input type="number" data-testid="test-pushups" value={st.tests.pushups_1min ?? ""} onChange={(e) => setT("pushups_1min", e.target.value)} className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl" placeholder="reps" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Prancha (segundos)</Label>
                <Input type="number" data-testid="test-plank" value={st.tests.plank_seconds ?? ""} onChange={(e) => setT("plank_seconds", e.target.value)} className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl" placeholder="s" />
              </div>
              <div className="sm:col-span-2 space-y-2">
                <Label className="text-slate-300 text-sm">Escolha um teste</Label>
                <div className="flex gap-2">
                  {[["abdominal", "Abdominal Remador"], ["corrida", "Corrida/Caminhada"]].map(([k, lb]) => (
                    <button key={k} type="button" data-testid={`test-choice-${k}`} onClick={() => setT("choice", k)}
                      className={`flex-1 h-11 rounded-xl text-sm font-medium border transition-colors ${st.tests.choice === k ? "bg-emerald-500 text-[#0A0D12] border-emerald-500" : "bg-[#0F1520] text-slate-300 border-[#263147] hover:border-[#34435f]"}`}>{lb}</button>
                  ))}
                </div>
                <Input data-testid="test-choice-value" value={st.tests.choice_value ?? ""} onChange={(e) => setT("choice_value", e.target.value)}
                  className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl" placeholder={st.tests.choice === "corrida" ? "Ex: 2 km em 12 min" : "Ex: 45 repetições"} />
              </div>
            </div>
          </SectionCard>

          {/* Block 3 - Bioimpedance */}
          <SectionCard icon={Activity} title="Bioimpedância" enabled={st.blocks.bioimpedance} onToggle={(v) => setBlock("bioimpedance", v)} testid="toggle-bioimpedance">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {bioInput("weight", "Peso atual", "kg", null)}
              {bioInput("height", "Altura", "cm", null)}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between"><Label className="text-slate-300 text-sm">IMC</Label><ColorBadge color={colorImc(imc)} /></div>
                <div className="relative">
                  <Input readOnly data-testid="bio-imc" value={imc ?? ""} className={`h-11 bg-[#0F1520] text-slate-100 rounded-xl pr-9 ${colorImc(imc) === "red" ? "border-red-500/60" : colorImc(imc) === "green" ? "border-emerald-500/50" : colorImc(imc) === "yellow" ? "border-amber-500/50" : "border-[#263147]"}`} placeholder="auto" />
                  <Calculator className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                </div>
              </div>
              {bioInput("body_fat", "Gordura Corporal", "%", (v) => colorFat(v, selected.gender, age))}
              {bioInput("muscle_mass", "Massa Muscular", "%", (v) => colorMuscle(v, selected.gender, age))}
              {bioInput("visceral_fat", "Gordura Visceral", "nível", colorVisceral)}
              {bioInput("bmr", "Taxa Metab. Repouso", "kcal", null)}
              {bioInput("body_age", "Idade Corporal", "anos", (v) => colorBodyAge(v, age))}
            </div>
          </SectionCard>

          {/* Block 4 - Health */}
          <SectionCard icon={HeartPulse} title="Condições de Saúde" enabled={st.blocks.health} onToggle={(v) => setBlock("health", v)} testid="toggle-health">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[["diet", "Faz dieta?"], ["diabetic", "Diabético?"]].map(([k, lb]) => (
                <div key={k} className="space-y-1.5">
                  <Label className="text-slate-300 text-sm">{lb}</Label>
                  <div className="flex gap-2">
                    {[[true, "Sim"], [false, "Não"]].map(([val, lbl]) => (
                      <button key={lbl} type="button" data-testid={`health-${k}-${val ? "sim" : "nao"}`} onClick={() => setH(k, val)}
                        className={`flex-1 h-11 rounded-xl text-sm font-medium border transition-colors ${st.health[k] === val ? "bg-emerald-500 text-[#0A0D12] border-emerald-500" : "bg-[#0F1520] text-slate-300 border-[#263147]"}`}>{lbl}</button>
                    ))}
                  </div>
                </div>
              ))}
              <div className="space-y-1.5">
                <Label className="text-slate-300 text-sm">Pressão</Label>
                <Select value={st.health.pressure} onValueChange={(v) => setH("pressure", v)}>
                  <SelectTrigger data-testid="health-pressure" className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                    <SelectItem value="baixa">Baixa</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="sm:col-span-3 space-y-1.5">
                <Label className="text-slate-300 text-sm">Restrições observadas</Label>
                <Textarea data-testid="health-restrictions" value={st.health.restrictions ?? ""} onChange={(e) => setH("restrictions", e.target.value)} className="bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl min-h-[80px]" placeholder="Observações do avaliador..." />
              </div>
            </div>
          </SectionCard>

          <ReferenceTable />

          {/* History + evolution */}
          {history.length > 0 && (
            <div className="bg-[#181F2C] border border-[#263147] rounded-3xl p-5">
              <h3 className="text-base font-semibold font-heading text-slate-100 mb-4 flex items-center gap-2"><ClipboardList className="w-4 h-4 text-emerald-400" /> Evolução e histórico</h3>
              <EvolutionChart history={[...history].reverse()} />
              <div className="mt-5 space-y-2" data-testid="assessment-history">
                {history.map((a) => (
                  <div key={a.id} className="flex items-center gap-3 bg-[#0F1520] border border-[#263147] rounded-xl px-4 py-2.5">
                    <span className="text-sm text-slate-300 flex-1 whitespace-nowrap">{a.created_at?.slice(0, 10)}</span>
                    <span className="text-xs text-slate-500 min-w-0 text-right">{a.bioimpedance?.weight ? `${a.bioimpedance.weight}kg` : ""} {a.bioimpedance?.imc ? `• IMC ${a.bioimpedance.imc}` : ""}</span>
                    <Button size="sm" variant="outline" data-testid={`history-pdf-${a.id}`} onClick={() => downloadPdf(a.id)} title={IOS ? "Ver PDF" : "Baixar PDF"} className="h-8 border-[#263147] bg-[#181F2C] text-slate-200 hover:bg-[#1E2738] rounded-lg"><FileDown className="w-3.5 h-3.5" />{IOS && <span className="ml-1 text-xs">Ver PDF</span>}</Button>
                    <Button size="sm" variant="outline" data-testid={`history-delete-${a.id}`} onClick={() => setPendingDelete(a)} className="h-8 border-[#263147] bg-[#181F2C] text-red-400 hover:bg-red-500/10 rounded-lg"><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3 sticky bottom-0 -mx-1 px-1 py-3 bg-[#0A0D12]/85 backdrop-blur-md rounded-t-2xl">
            <Button data-testid="assessment-save-button" onClick={save} disabled={saving} className="flex-1 h-14 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold text-base rounded-xl shadow-lg shadow-emerald-500/20">
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Save className="w-5 h-5 mr-2" /> Salvar avaliação</>}
            </Button>
            <Button data-testid="assessment-pdf-button" onClick={() => downloadPdf()} disabled={saving} variant="outline" className="h-14 px-6 border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 rounded-xl font-semibold">
              <FileDown className="w-5 h-5 mr-2" /> {IOS ? "Ver PDF" : "Gerar Relatório em PDF"}
            </Button>
          </div>
        </div>
      )}

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Excluir avaliação?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {pendingDelete && `A avaliação de ${pendingDelete.created_at?.slice(0, 10)} será removida permanentemente.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-assessment" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-xl">Excluir</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
